// VertraegeTabelleRedesign.tsx — eigenständige Verträge-Seite (/vertraege) im
// „Refined Classic"-Redesign auf der generischen DataTable-Engine.
//
// Spalten (Vertragsaufstellung-Set, ohne Objektkategorie): Vertragsart · Kontakt
// (Firma/Privat) · Verantwortlichkeit · Bezeichnung (Produktname) · Gesellschaft
// (Logo) · Status (contractPhase) · Prämie · Beginn · Ablauf · Vertragsnummer.
// Scope-Toggle (alle/team/persönlich), serverseitige Suche, Mobile-Karten.

import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { I } from "./kvpIcons";
import { DataTable, ColumnsMenu, type Column, type SortState, type DateRangeValue, Dash } from "./table";
import { buildContractFilters } from "./contractFilterParams";
import "./contactRedesign.css";
import { apiFetch } from "../../utils/apiFetch";
import { defaultContactScope } from "../../utils/contactScope";
import { useDebouncedValue } from "../../utils/useDebouncedValue";
import { useInfiniteRows } from "../../hooks/useInfiniteRows";
import { useResponsive } from "../../hooks/useResponsive";
import { useAuth } from "../../context/AuthContext";
import type { ContactScope } from "../contacts/ContactScopeToggle";
import {
  KontaktPill,
  VerantwortlichkeitCell,
  GesellschaftLogo,
  formatEuro,
  formatDate,
  produktBezeichnung,
  risikoobjektLabel,
} from "./contractRiskCells";

type Row = any;

const premiumOf = (r: Row) => (r?.premiumAnnualGross ?? r?.annualFee ?? null);

const VertraegeTabelleRedesign: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, teams } = useAuth();
  const hasTeam = (teams?.length ?? 0) > 0;
  const { isPhone } = useResponsive();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [sort, setSort] = useState<SortState | null>(null);
  const [scope, setScope] = useState<ContactScope>(() => defaultContactScope(isAdmin, hasTeam));
  useEffect(() => {
    if (scope === "team" && !hasTeam) setScope("all");
  }, [scope, hasTeam]);

  const scopeOptions = useMemo(() => {
    const opts: { value: ContactScope; label: string }[] = [{ value: "all", label: "Alle" }];
    if (hasTeam) opts.push({ value: "team", label: "Team" });
    opts.push({ value: "own", label: "Persönlich" });
    return opts;
  }, [hasTeam]);

  // Spaltenfilter (serverseitig): Facetten-Optionen + Filter-State.
  const [filterValues, setFilterValues] = useState<Record<string, string | boolean | undefined>>({});
  const [dateRangeValues, setDateRangeValues] = useState<Record<string, DateRangeValue | undefined>>({});
  const [facets, setFacets] = useState<{ status: string[]; gesellschaft: string[]; vertragsart: string[] }>({
    status: [],
    gesellschaft: [],
    vertragsart: [],
  });
  useEffect(() => {
    apiFetch<any>("contracts/facets")
      .then(({ data }) => data && setFacets(data))
      .catch(() => {});
  }, []);
  const updateFilter = useCallback(
    (id: string, v: string | boolean | undefined) => setFilterValues((p) => ({ ...p, [id]: v })),
    [],
  );
  const updateDateRange = useCallback(
    (id: string, r: DateRangeValue) => setDateRangeValues((p) => ({ ...p, [id]: r })),
    [],
  );
  const contractFilters = useMemo(
    () => buildContractFilters({ filterValues, dateRangeValues }),
    [filterValues, dateRangeValues],
  );

  // Spalten-Sichtbarkeit / -Breiten / -Collapse (In-Memory, pro Sitzung).
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [collapsedColumns, setCollapsedColumns] = useState<string[]>([]);
  const toggleCollapsed = useCallback(
    (id: string) => setCollapsedColumns((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])),
    [],
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  const { rows, hasMore, loading, loadMore } = useInfiniteRows<Row>({
    getKey: (r) => r.id,
    deps: [debouncedSearch, sort?.field ?? "", sort?.direction ?? "", scope, JSON.stringify(contractFilters)],
    fetchPage: async ({ limit, offset, signal }) => {
      const hasFilters = Object.keys(contractFilters).length > 0;
      const { data } = await apiFetch<any[]>("contracts", {
        signal,
        params: {
          limit,
          offset,
          search: debouncedSearch,
          scope,
          sort: sort?.field ?? "",
          direction: sort?.direction ?? "",
          ...(hasFilters ? { filters: JSON.stringify(contractFilters) } : {}),
        },
      });
      // /contracts meldet X-Total-Count = Seitenlänge (nicht Gesamtzahl) →
      // total:null, damit useInfiniteRows über die Seitenfüllung nachlädt.
      return { rows: Array.isArray(data) ? data : [], total: null };
    },
  });

  const openContract = (r: Row) => navigate(`/contracts/${r.id}`);

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        id: "Vertragsart",
        header: "Vertragsart",
        pinned: true,
        frozen: true,
        minWidth: 180,
        resizable: true,
        filter: { kind: "dropdown", options: facets.vertragsart },
        // Vertragsart = die durch das Sparten-Mapping gesetzte Sparte
        // (insuranceProduct), ersatzweise der rohe GDV-branch. Bewusst KEIN
        // Fallback auf den Vertragstyp „Einzel-/Sammelvertrag" — das ist der
        // contractType, nicht die Vertragsart/Sparte.
        cell: (r) => r.insuranceProduct?.name || r.branch || <Dash />,
      },
      {
        id: "Risikoobjekt",
        header: "Risikoobjekt",
        hideable: true,
        collapsible: true,
        minWidth: 180,
        resizable: true,
        filter: { kind: "text" },
        cell: (r) => risikoobjektLabel(r) || <Dash />,
      },
      {
        id: "Kontakt",
        header: "Kontakt",
        minWidth: 200,
        resizable: true,
        filter: { kind: "text" },
        cell: (r) => <KontaktPill contact={r.contact} />,
      },
      {
        id: "Verantwortlichkeit",
        header: "Verantwortlichkeit",
        hideable: true,
        collapsible: true,
        minWidth: 160,
        cell: (r) => <VerantwortlichkeitCell contact={r.contact} />,
      },
      {
        id: "Bezeichnung",
        header: "Bezeichnung",
        hideable: true,
        collapsible: true,
        minWidth: 160,
        resizable: true,
        filter: { kind: "text" },
        cell: (r) => produktBezeichnung(r) || <Dash />,
      },
      {
        id: "Gesellschaft",
        header: "Gesellschaft",
        hideable: true,
        width: 200,
        resizable: true,
        filter: { kind: "dropdown", options: facets.gesellschaft },
        cell: (r) => <GesellschaftLogo id={r.gesellschaftId ?? r.gesellschaft?.id} name={r.gesellschaft?.name} />,
      },
      {
        id: "Status",
        header: "Status",
        hideable: true,
        collapsible: true,
        filter: { kind: "dropdown", options: facets.status },
        cell: (r) => r.contractPhase?.label || <Dash />,
      },
      {
        id: "Praemie",
        header: "Prämie",
        align: "right",
        sortable: true,
        sortField: "premium",
        hideable: true,
        cell: (r) => {
          const v = premiumOf(r);
          return v != null ? formatEuro(v) : <Dash />;
        },
      },
      {
        id: "Beginn",
        header: "Beginn",
        sortable: true,
        sortField: "begin",
        hideable: true,
        collapsible: true,
        filter: { kind: "dateRange" },
        cell: (r) => formatDate(r.beginPolicyTerm) || <Dash />,
      },
      {
        id: "Ablauf",
        header: "Ablauf",
        sortable: true,
        sortField: "end",
        hideable: true,
        collapsible: true,
        filter: { kind: "dateRange" },
        cell: (r) => formatDate(r.endPolicyTerm) || <Dash />,
      },
      {
        id: "Vertragsnummer",
        header: "Vertragsnummer",
        sortable: true,
        sortField: "contractNbr",
        minWidth: 140,
        resizable: true,
        cell: (r) => r.contractNbr || <Dash />,
      },
    ],
    [facets],
  );

  const scopeSeg = (
    <div className="rbac-seg" role="group" aria-label="Sichtbarkeitsbereich">
      {scopeOptions.map((o) => (
        <button
          key={o.value}
          type="button"
          className={"rbac-seg-btn" + (scope === o.value ? " on" : "")}
          aria-pressed={scope === o.value}
          onClick={() => setScope(o.value)}
        >
          <span className="rbac-dot" />
          {o.label}
        </button>
      ))}
    </div>
  );

  const tableSubBar = (
    <div className="kt-subbar">
      <ColumnsMenu
        columns={columns}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={toggleCollapsed}
      />
    </div>
  );

  const cardList = (
    <div className="ct-cards" id="vertraege-scroll" ref={scrollRef}>
      {rows.length === 0 ? (
        <div className="empty">{loading ? "Lade Verträge …" : "Keine Verträge gefunden."}</div>
      ) : (
        rows.map((r) => (
          <div key={r.id} className="ct-card" onClick={() => openContract(r)}>
            <div className="ct-card-head">
              <div className="ct-card-id">
                <div className="ct-card-name">
                  {r.insuranceProduct?.name || r.branch || "Vertrag"}
                </div>
                <div style={{ fontSize: 12, color: "#8a7b85" }}>{r.contractNbr || "—"}</div>
              </div>
              <GesellschaftLogo id={r.gesellschaftId ?? r.gesellschaft?.id} name={r.gesellschaft?.name} />
            </div>
            <div className="ct-card-body">
              <div className="ct-row"><span className="ct-k">Risikoobjekt</span><span className="ct-v">{risikoobjektLabel(r) || "—"}</span></div>
              <div className="ct-row"><span className="ct-k">Kontakt</span><span className="ct-v"><KontaktPill contact={r.contact} /></span></div>
              <div className="ct-row"><span className="ct-k">Bezeichnung</span><span className="ct-v">{produktBezeichnung(r) || "—"}</span></div>
              <div className="ct-row"><span className="ct-k">Status</span><span className="ct-v">{r.contractPhase?.label || "—"}</span></div>
              <div className="ct-row"><span className="ct-k">Prämie</span><span className="ct-v">{premiumOf(r) != null ? formatEuro(premiumOf(r)) : "—"}</span></div>
            </div>
          </div>
        ))
      )}
      {hasMore ? (
        <button className="ct-more" onClick={() => loadMore()} disabled={loading}>
          {loading ? "Lädt …" : "Mehr laden"}
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="kvp-rd kvp-rd-table-page">
      <div className="bar">
        <div className="search">
          {I.search}
          <input
            placeholder="Suche in Verträgen …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="spacer" style={{ flex: 1 }} />
        {scopeSeg}
      </div>

      {isPhone ? (
        cardList
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={openContract}
          emptyText={loading ? "Lade Verträge …" : "Keine Verträge gefunden."}
          sort={sort}
          onSortChange={setSort}
          filterValues={filterValues}
          onFilterChange={updateFilter}
          dateRangeValues={dateRangeValues}
          onDateRangeChange={updateDateRange}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          columnWidths={columnWidths}
          onColumnWidthsChange={setColumnWidths}
          collapsedColumns={collapsedColumns}
          onToggleCollapsed={toggleCollapsed}
          onLoadMore={loadMore}
          hasMore={hasMore}
          loading={loading}
          scrollContainerId="vertraege-scroll"
          scrollRef={scrollRef}
          aboveTable={tableSubBar}
        />
      )}
    </div>
  );
};

export default VertraegeTabelleRedesign;
